import { inject, BindingEngine } from 'aurelia-framework';
import { DaiatariService } from 'pages/daiatari/service';
import { AssignedProjectService } from 'pages/daiatari/assigned-projects/service';
import { SnapshotTypeService } from 'pages/system-fields/snapshot-types/service';
import { QuoteService } from 'pages/quotes/service';
import { StatusService } from 'pages/system-fields/statuses/service';
import { formatTableDate } from 'resources/utility';
import toastr from 'toastr';
import { delegate } from 'tippy.js';

@inject(DaiatariService, AssignedProjectService, StatusService, SnapshotTypeService, QuoteService, BindingEngine)
export class DaiatariReport {
    constructor(daiatariService, assignedProjectService, statusService, snapshotTypeService, quoteService, bindingEngine) {
        this.daiatariService = daiatariService;
        this.assignedProjectService = assignedProjectService;
        this.statusService = statusService;
        this.snapshotTypeService = snapshotTypeService;
        this.quoteService = quoteService;

        this.filterOptions = {
            assignedProjectId: 0,
            statusId: 0,
            searchText: null
        };

        this.snapshotFilterOptions = {
            snapshotTypeId: null,
            productionType: null,
            effectiveDate: null,
            showDeleted: false
        }

        this.reportSettings = {
            addChildParts: true,
            sortBy: 'AssignedProject',
            thenBy: 'PartNumber'
        }

        this.reportDaiatariIds = [];

        bindingEngine.propertyObserver(this.filterOptions, 'assignedProjectId').subscribe(() => {
            this.grid.refresh();
        });

        bindingEngine.propertyObserver(this.filterOptions, 'statusId').subscribe(() => {
            this.grid.refresh();
        });

        bindingEngine.propertyObserver(this.snapshotFilterOptions, 'snapshotTypeId').subscribe(() => {
            this.reloadSnapshots();
        });

        bindingEngine.propertyObserver(this.snapshotFilterOptions, 'productionType').subscribe(this.reloadSnapshots);

        bindingEngine.propertyObserver(this.snapshotFilterOptions, 'effectiveDate').subscribe(() => {
            this.reloadSnapshots();
        });

        this.columns = [
            { caption: 'QS#', dataField: 'quoteId' },
            { caption: 'Plant', dataField: 'plantName' },
            { caption: 'Customer', dataField: 'customerName' },
            { caption: 'Project', dataField: 'projectNumber' },
            { caption: 'Part Number', dataField: 'partNumber' },
            { caption: 'Ratio', dataField: 'ratio' },
            { caption: 'Assigned Project', dataField: 'assignedProjectName' },
            { caption: 'Selected', cellTemplate: (container, options) => {
                const isChecked = this.isSelected(options.data.id, null);
                $("<div>").dxCheckBox({
                    value: isChecked,
                    onValueChanged: (e) => {
                        if (e.value) {
                            this.updateSelection(options.data.id, null);
                        } else {
                            const wasSelected = this.isSelected(options.data.id, null);
                            this.reportDaiatariIds = this.reportDaiatariIds.filter(item => item.daiatariId !== options.data.id);
                            if (wasSelected) {
                                this.repaintChangedRows(options.data.id, null, null);
                            }
                        }
                    }
                }).appendTo(container);
            }}
        ];
    }

    attached() {
      delegate('body', {
        target: '.snapshot-description',
        content(reference) {
            const message = reference.getAttribute('data-message');
            return message;
        }
      });
    }

    link = (options, event) => {
        if (event.target.checked) {
            if (options.data.snapshotId != null) {
                const existing = this.reportDaiatariIds.find(x => x.snapshotId == options.data.id);
                if (existing) {
                    existing.snapshotId = options.data.id;
                    existing.quoteId = null;
                } else {
                    this.reportDaiatariIds.push({ snapshotId: options.data.id, daiatariId: options.data.daiatariId });
                }
            } else {
                const existing = this.reportDaiatariIds.find(x => x.daiatariId == options.data.id);
                if (existing) {
                    existing.daiatariId = options.data.id;
                    existing.snapshotId = null;
                } else {
                    this.reportDaiatariIds.push({ snapshotId: null, daiatariId: options.data.id });
                }
            }
        } else {
            if (options.data.snapshotId != null) {
                const index = this.reportDaiatariIds.findIndex(x => x.snapshotId == options.data.id);
                if (index != -1) {
                    this.reportDaiatariIds.splice(index, 1);
                }
            } else {
                const index = this.reportDaiatariIds.findIndex(x => x.daiatariId == options.data.id);
                if (index != -1) {
                    this.reportDaiatariIds.splice(index, 1);
                }
            }
        }
        this.grid.refresh();
    };

    async selectAll(event) {
        if (event.target.checked) {
            const filter = this.grid.getFilter();
            this.filterOptions.searchText = filter;
            const daiatariIds = await this.daiatariService.getDaiatariIdsForFilter(this.filterOptions);
            this.reportDaiatariIds.splice(0, this.reportDaiatariIds.length);
            this.reportDaiatariIds.push(...daiatariIds.map(x => ({ daiatariId: x.daiatariId, snapshotId: null })));
        } else {
            this.reportDaiatariIds.splice(0, this.reportDaiatariIds.length);
        }

        this.grid.refresh();
    }

    async generateHTMLReport() {
        if (this.reportDaiatariIds.length == 0) {
            toastr.error('Please select quotes for the report');
            return;
        }

        window.localStorage.setItem('daiatary_report_settings', JSON.stringify(this.reportSettings))
        window.localStorage.setItem('daiatary_report_daiatariIds', JSON.stringify(this.reportDaiatariIds));

        window.open('daiatari/report/html?print=true');
    }

    async generateExcelReport() {
        this.isDownloadingExcelFile = true;
        await this.daiatariService.generateExcelReport(this.reportSettings, this.reportDaiatariIds);
        this.isDownloadingExcelFile = false;
    }

    async generateAPRSteelMasterReport() {
        this.isDownloadingARPFile = true;
        await this.daiatariService.generateAPRSteelMasterReport(this.reportSettings, this.reportDaiatariIds);
        this.isDownloadingARPFile = false;
    }

    generateSnapshotMasterDetail = () => {
        return {
            enabled: true,
            autoExpandAll: false,
            template: (container, options) => {
                $('<h6>')
                    .addClass('master-detail-caption')
                    .text(`Snapshots`)
                    .appendTo(container);
                const gridContainer = $('<div>');
                gridContainer
                    .dxDataGrid({
                        elementAttr: {
                            id: `snapshot-grid-${options.data.id}`
                        },
                        columnAutoWidth: true,
                        showBorders: true,
                        columns: [
                            { dataField: 'description', cellTemplate: (container, options) => {
                                const info = document.createElement('i');
                                info.classList.add('bx', 'bx-info-circle', 'bx-sm', 'snapshot-description');
                                info.setAttribute('data-message', options.data.description);
                                container.append(info);
                            }},
                            { dataField: 'snapshotType' },
                            { dataField: 'productionType' },
                            { dataField: 'effectiveDate', customizeText: formatTableDate },
                            { dataField: 'poNumber' },
                            { dataField: 'revision' },
                            { dataField: 'createdAt', customizeText: formatTableDate, sortOrder: 'desc' },
                            { dataField: 'createdBy' },
                            { caption: 'Action', cellTemplate: (container, detailOptions) => {
                              const isChecked = this.isSelected(detailOptions.data.daiatariId, detailOptions.data.id);
                              $("<div>").dxCheckBox({
                                  value: isChecked,
                                  onValueChanged: (e) => {
                                    if (e.value) {
                                        this.updateSelection(
                                            detailOptions.data.daiatariId,
                                            detailOptions.data.id
                                        );
                                    } else {
                                        const wasSelected = this.isSelected(detailOptions.data.daiatariId, detailOptions.data.id);
                                        this.reportDaiatariIds = this.reportDaiatariIds.filter(item =>
                                            !(item.daiatariId === detailOptions.data.daiatariId &&
                                              item.snapshotId === detailOptions.data.id)
                                        );
                                        if (wasSelected) {
                                            this.repaintChangedRows(detailOptions.data.daiatariId, detailOptions.data.id, null);
                                        }
                                    }
                                }
                                }).appendTo(container);
                            }}
                        ],
                        dataSource: new DevExpress.data.DataSource({
                            load: async (loadOptions) => {
                                 const snapshots = await this.quoteService.filterSnapshots(JSON.stringify(loadOptions), [options.data.quoteId, this.snapshotFilterOptions])
                                 return snapshots.data.map(x => ({
                                    ...x,
                                    daiatariId: options.data.id
                                 }));
                            }
                        }),
                        onContentReady: (e) => {
                            e.element.find(".dx-datagrid-text-content").removeClass("dx-text-content-alignment-left");
                        },
                    }).appendTo(container);
            }
        };
    }

    isSelected(daiatariId, snapshotId = null) {
        return this.reportDaiatariIds.some(x => 
            x.daiatariId == daiatariId &&
            x.snapshotId == snapshotId
        );
    }

    
    updateSelection = (daiatariId, snapshotId = null) => {
        const previous = this.reportDaiatariIds.find(item => item.daiatariId === daiatariId);
        this.reportDaiatariIds = this.reportDaiatariIds.filter(item => item.daiatariId !== daiatariId);
        this.reportDaiatariIds.push({ daiatariId, snapshotId });
        this.repaintChangedRows(daiatariId, previous?.snapshotId, snapshotId);
    }
    
    repaintChangedRows = (daiatariId, oldSnapshotId, newSnapshotId) => {
        this.grid.getVisibleRows().forEach(row => {
            if (row.data.id === daiatariId) {
                this.grid.repaintRows([row.rowIndex]);
    
                if (row.isExpanded) {
                    const detailGrid = $(`#snapshot-grid-${daiatariId}`).dxDataGrid("instance");
                    if (detailGrid) {
                        detailGrid.getVisibleRows().forEach(detailRow => {
                            if (
                                detailRow.data.id === oldSnapshotId ||
                                detailRow.data.id === newSnapshotId
                            ) {
                                detailGrid.repaintRows([detailRow.rowIndex]);
                            }
                        });
                    }
                }
            }
        });
    }   
    
    reloadSnapshots = () => {
        this.grid.getVisibleRows().forEach(row => {
            if (row.isExpanded) {
                const detailGrid = $(`#snapshot-grid-${row.data.id}`).dxDataGrid("instance");
                if (detailGrid) {
                    detailGrid.refresh();
                }
            }
        });
    }
}